import React, { useCallback } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { Banner } from '@atlaskit/navigation-system';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation';
import type { GlobalSideNavigationProps } from '@atlassian/global-side-navigation/types';
import GlobalTopNavigation from '@atlassian/global-top-navigation';
import type { GlobalTopNavigationProps } from '@atlassian/global-top-navigation/common/types';

import type { AnyGlobalRootQueryData, AppChromeProps, BannerProps } from '../common/types';

import type { AppChrome$key } from './__generated__/AppChrome.graphql';

/**
 * AppChrome renders the app-level navigation slots (Banner, TopNav and SideNav)
 * and wires GlobalTopNavigation (GTN) as the base implementation of the TopNav.
 *
 * The `queryRef` resolved by `useRelayQuery` is injected into the base TopNav;
 * consumers customize the slot via the `TopNavSlot` render prop. The same raw,
 * `Query`-level `queryRef` is also handed to `TopNavSlot`, so a consumer whose
 * own `useRelayQuery` spreads additional fragments (alongside `...AppChrome`)
 * can unmask their own data with `useFragment` inside the slot. When no
 * `AppHomeSlot` is supplied, GTN renders a default App Home from `app`/`appLabel`.
 */
export function AppChrome<Q extends AnyGlobalRootQueryData = AnyGlobalRootQueryData>(
	props: AppChromeProps<Q>,
): React.JSX.Element {
	di(GlobalSideNavigation, GlobalTopNavigation);

	const { app, appLabel, customizationStore, BannerSlot, SideNavSlot, TopNavSlot, useRelayQuery } =
		props;

	// The consumer-provided hook returns the query result whose only node is
	// `...AppChrome`. Any query variables (e.g. the ARIs for personal navigation
	// customization) are baked into the consumer's `useRelayQuery` implementation.
	const queryRef = useRelayQuery();

	// Read the `AppChrome` fragment to unmask the child fragment spreads (e.g.
	// `...GlobalTopNavigation`). The resulting reference is what GTN/GSN expect
	// as their own `queryRef`.
	const initialNavState = useFragment<AppChrome$key>(
		graphql`
			fragment AppChrome on Query {
				...GlobalTopNavigation
			}
		`,
		queryRef,
	);

	// The reason we use useCallback (instead of module-level function component)
	// is that we want to pass *some* data from this scope into
	// the components' scope (e.g. app, appLabel, initialNavState).

	const BaseTopNav = useCallback(
		(wrapperProps: Partial<GlobalTopNavigationProps>) => {
			// GTN requires a non-null `queryRef`. When no navigation data has been
			// loaded (the default/no-op `useRelayQuery`), don't render the base
			// TopNav — a customizing consumer is expected to supply its own
			// `useRelayQuery` (and therefore a valid fragment reference).
			if (!initialNavState) {
				return null;
			}

			return (
				<GlobalTopNavigation
					app={app}
					appLabel={appLabel}
					customizationStore={customizationStore}
					testId="global-root-topnav"
					{...wrapperProps}
					queryRef={initialNavState}
				/>
			);
		},
		[app, appLabel, customizationStore, initialNavState],
	);

	const BaseSideNav = useCallback(
		(wrapperProps: Partial<GlobalSideNavigationProps>) => {
			return (
				<GlobalSideNavigation
					app={app}
					appLabel={appLabel}
					customizationStore={customizationStore}
					testId="global-root-sidenav"
					forYou={{}}
					items={{}}
					{...wrapperProps}
				/>
			);
		},
		[app, appLabel, customizationStore],
	);

	return (
		<>
			{BannerSlot && <BannerSlot BannerComponent={BaseBanner} />}
			{TopNavSlot !== null &&
				(TopNavSlot ? (
					/* Pass the "unmasked"/root queryRef so that the caller can access the root-query's fragments (the caller
					   knows what data to pass to the components).
					 */
					<TopNavSlot TopNavComponent={BaseTopNav} queryRef={queryRef} />
				) : (
					<BaseTopNav />
				))}
			{SideNavSlot !== null &&
				(SideNavSlot ? <SideNavSlot SideNavComponent={BaseSideNav} /> : <BaseSideNav />)}
		</>
	);
}

function BaseBanner(wrapperProps: BannerProps) {
	return <Banner {...wrapperProps} />;
}
